import React, { useCallback, useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowModesModel,
} from "@mui/x-data-grid";
import { Container } from "@mui/material";
import { useSnackbar } from "notistack";
import { parseDate } from "../../util/date";
import { Webmap } from "../../api/portal";
import { WebmapEndpoint } from "../../api/webmaps";
import BooleanValue from "../BooleanValue";
import { makeColumn } from "../../util/tableUtils";

const columns: GridColDef[] = [
  { field: "id", headerName: "Webmap", type: "number", width: 100 },
  { field: "ars_lnd", headerName: "Land", width: 65 },
  { field: "kunden_id", headerName: "Kunde", width: 75 },
  { field: "projekt_id", headerName: "Projekt", width: 75 },
  {
    field: "intern",
    headerName: "intern",
    type: "boolean",
    width: 70,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.intern} />
    ),
  },
  { field: "bezeichner", headerName: "Bezeichner", width: 250 },
  { field: "map_setup", headerName: "Setup", width: 70 },
  { field: "kbs", headerName: "KBS", width: 70 },
  {
    field: "is_3d",
    headerName: "3D",
    type: "boolean",
    width: 60,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_3d} />
    ),
  },
  {
    field: "aktiv",
    headerName: "aktiv",
    type: "boolean",
    width: 70,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.aktiv} />
    ),
  },
  { field: "faktura", headerName: "Faktura" },
  { field: "dauer", headerName: "Dauer", type: "number", width: 80 },
  {
    field: "fs_start",
    headerName: "Start",
    type: "date",
    valueGetter: (params) => new Date(params.row.fs_start),
    valueSetter: (params) => {
      const fs_start = parseDate(params.value);
      const newParams = {
        ...params.row,
        fs_start,
      };

      return newParams;
    },
  },
  {
    field: "fs_ende",
    headerName: "Ende",
    type: "date",
    valueGetter: (params) => new Date(params.row.fs_ende),
    valueSetter: (params) => {
      const fs_ende = parseDate(params.value);
      const newParams = {
        ...params.row,
        fs_ende,
      };

      return newParams;
    },
  },
  {
    field: "is_tagged",
    headerName: "markiert",
    type: "boolean",
    width: 75,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_tagged} />
    ),
  },
  {
    field: "is_revised",
    headerName: "korrigiert",
    type: "boolean",
    width: 75,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_revised} />
    ),
  },
  {
    field: "is_checked",
    headerName: "geprüft",
    type: "boolean",
    width: 75,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_checked} />
    ),
  },
  {
    field: "is_locked",
    headerName: "gesperrt",
    type: "boolean",
    width: 75,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_locked} />
    ),
  },
  {
    field: "is_removed",
    headerName: "gelöscht",
    type: "boolean",
    width: 75,
    renderCell: (params: GridRenderCellParams<Webmap>) => (
      <BooleanValue value={params.row.is_removed} />
    ),
  },
];

const WebmapTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [webMapData, setWebMapData] = useState<Omit<Webmap, "changed_by">[]>(
    []
  );
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const handleUpdate = (row: Omit<Webmap, "changed_by">) => {
    setIsLoading(true);

    WebmapEndpoint.update(row).then(() => {
      enqueueSnackbar(`Webmap ${row.id} aktualisiert`, {
        key: `row-updated-${row.id}`,
      });
      setIsLoading(false);
    });
  };

  const fetchWebmaps = useCallback(() => {
    WebmapEndpoint.get()
      .then((res) => res.data)
      .then((webmap: Webmap) => {
        console.log(webmap);
        setWebMapData([webmap]);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchWebmaps();
  }, [fetchWebmaps]);

  return (
    <Container
      style={{ width: "100%" }}
      sx={{ m: 0, p: 0, height: "calc(100vh - 157px)" }}
      maxWidth={false}
      disableGutters
    >
      <DataGrid
        rows={webMapData}
        columns={columns.map((col) => {
          return makeColumn(col);
        })}
        rowModesModel={rowModesModel}
        onRowModesModelChange={(model) => {
          setRowModesModel(model);
        }}
        editMode="row"
        processRowUpdate={(row: Omit<Webmap, "changed_by">) => {
          handleUpdate(row);
          return row;
        }}
        onProcessRowUpdateError={() => {}}
        loading={isLoading}
        pageSizeOptions={[1]}
        paginationModel={{
          page: 0,
          pageSize: 1,
        }}
      />
    </Container>
  );
};

export default WebmapTable;
